import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Avatar,
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
  useMediaQuery,
} from 'native-base';
import Svg from 'react-native-svg';
import CryptoJS from 'crypto-js';
import CommonHeading from '../../../components/headings';
import ToggleButton from '../../../components/toggle';
import ToggleSwitch from '../../../components/toggleSwitch';
import {
  editIconDetailsPage,
  noPhotosUploadedSVG,
  speakerNoPersonSVG,
  deleteCardsIconSVG,
} from '../../../utils/svg';
import config from '../../../utils/config';
import { COMMONFONTSIZE, COMMONFONTWEIGHT } from '../../../utils/fonts';
import { COLORS } from '../../../utils/colors';
import { MOBILE_VIEW_WIDTH } from '../../../utils/screens';
import CustomButton from '../../../components/button';
import CustomModal from '../../../components/modal';
import MobileModelSheet from '../../../components/actionSheet';
import DividerLine from '../../../components/divider';
import { Toaster } from '../../../components/toaster';
import { LoaderAnimation } from '../../../components/spinner';
import AccordionExample from '../../../components/eventBreakDownAccordion';
import CreateEventBreakDown from './modalChildren';
import { EventPlanDetailsCards } from './modalChildren/eventPlanDetailsCard';
import { addEventPlanAPI, editEventPlanAPI } from '../../../api/apis/event';
import { addEventSessionAPI, editEventSessionAPI, getEventSessionsAPI, deleteEventSessionAPI, getFoodMenuAPI, updateFoodMenuStatusAPI } from '../../../api/apis/quicklinks';
import { uploadFileAPI, requestEventAccessAPI, getEventUserRequestStatusAPI } from '../../../api/apis/workflow';
import { getUserInformations } from '../../../api/apis/login';
import { hasPermission, statusMap } from '../../../utils/permission';
import { SightSeeingCards } from './modalChildren/sightseeingCards';
import BulkUploadMenuModal from './modalChildren/bulkUploadMenuModal';
import FoodMenuCard from './modalChildren/foodMenuCard';
import NoDataFound from '../../../components/noDataFound';
import './style.css';

const EventBreakDownDetails = ({
  eventsDetail,
  getEventDetails,
  setTabActive,
  selected,
}) => {
  const childRef = useRef();
  const childref = useRef();
  const agendaRef = useRef();
  const toaster = Toaster();
  const [tabActiveButton, setTabActiveButton] = useState('Day 1');
  const [showModal, setShowModal] = useState(false);
  const [MobileWindow] = useMediaQuery({ maxWidth: MOBILE_VIEW_WIDTH });
  const activeTab = useSelector((state) => {
    return state.event.activeTab;
  });
  const eventTab = activeTab === 'event' ? 'Event' : 'MICE';
  const [defaultData, setDefaultData] = useState([]);
  const [noOfDays, setNoOfDayss] = useState(0);
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedDay, setSelectedDay] = useState({});
  const [selectedSpeaker, setSelectedSpeaker] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const headings = [
    { name: 'Agenda', value: 'agenda' },
    { name: 'Speakers Details', value: 'speaker' },
    { name: 'Food Menu', value: 'menu' },
    { name: 'Sightseeing', value: 'sightseeing' },
  ];
  const containerRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [forceRemount, setForceRemount] = useState(false);
  const [currentDate, setCurrentDate] = useState([]);
  const [eventBreakError, setEventBreakError] = useState([]);
  const [foodMenuData, setFoodMenuData] = useState(null);
  const [isLoadingFoodMenu, setIsLoadingFoodMenu] = useState(false);
  const foodMenuFetchRef = useRef(null);
  const [isFoodMenuVisible, setIsFoodMenuVisible] = useState(true);

  // Function to clear food menu cache and refetch
  const refreshFoodMenuData = async () => {
    // Clear the cache to force a fresh fetch
    foodMenuFetchRef.current = null;
    await fetchFoodMenuData();
  };

  // Function to handle toggle and call API
  const handleFoodMenuToggle = async (newVisibility) => {
    try {
      // Calculate the date for the current tab
      const calculateEventDate = () => {
        if (eventsDetail?.from_date && tabActiveButton) {
          const eventStartDate = new Date(eventsDetail.from_date);
          const dayNumber = parseInt(tabActiveButton.split(' ')[1]) || 1;
          const eventDate = new Date(eventStartDate);
          eventDate.setDate(eventStartDate.getDate() + dayNumber - 1);
          return eventDate.toISOString().split('T')[0];
        }
        return eventsDetail?.from_date;
      };

      const dateOfEvent = calculateEventDate();
      
      // Update the state immediately for better UX
      setIsFoodMenuVisible(newVisibility);
      
      // Call the API
      const response = await updateFoodMenuStatusAPI(
        eventsDetail?._id,
        dateOfEvent,
        newVisibility
      );
      
      if (response.error) {
        // Revert the state if API call fails
        setIsFoodMenuVisible(!newVisibility);
        toaster(response.message || 'Failed to update menu status', 'error', 'error');
      } else {
        toaster(response?.data?.data?.message || 'Menu status updated successfully', 'success', 'success');
        
        // Refetch food menu data to get updated menu after toggle
        await refreshFoodMenuData();
      }
    } catch (error) {
      // Revert the state if API call fails
      setIsFoodMenuVisible(!newVisibility);
      toaster('Failed to update menu status', 'error', 'error');
      console.error('Error updating food menu status:', error);
    }
  };

  // Access control states
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessModalType, setAccessModalType] = useState('');
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);

  // Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Bulk upload modal states
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  // Get userInfo from sessionStorage
  const bytes = CryptoJS.AES.decrypt(
    sessionStorage.getItem("content"),
    config.userKey
  );
  const jsonString = bytes.toString(CryptoJS.enc.Utf8);
  const userInfo = JSON.parse(jsonString);

  // Function to fetch food menu data
  const fetchFoodMenuData = async () => {
    if (!eventsDetail?._id || !tabActiveButton) {
      return;
    }
    
    // Prevent duplicate calls for the same event and date
    const fetchKey = `${eventsDetail._id}-${tabActiveButton}`;
    if (foodMenuFetchRef.current === fetchKey) {
      return;
    }
    
    foodMenuFetchRef.current = fetchKey;
    
    try {
      setIsLoadingFoodMenu(true);
      
      // Calculate the date based on event start date and current tab
      const calculateEventDate = () => {
        if (eventsDetail?.from_date && tabActiveButton) {
          const eventStartDate = new Date(eventsDetail.from_date);
          const dayNumber = parseInt(tabActiveButton.split(' ')[1]) || 1;
          const eventDate = new Date(eventStartDate);
          eventDate.setDate(eventStartDate.getDate() + (dayNumber - 1));
          return eventDate.toISOString().split('T')[0];
        }
        return new Date().toISOString().split('T')[0];
      };

      const eventDate = calculateEventDate();      
      const response = await getFoodMenuAPI(eventsDetail._id, eventDate);      
      // Handle the nested response structure: response.data.data.data
      if (response && !response.error && response.data && response.data.data && response.data.data.data) {
        const menuData = response.data.data.data;
        // Check if data is an empty object {} or has no uploaded_at (means no data was ever uploaded)
        if (Object.keys(menuData)?.length === 0 || !menuData?.uploaded_at) {
          setFoodMenuData(null);
          setIsFoodMenuVisible(true);
        } else {
          // Set the toggle state based on to_show from API response
          // If to_show is not provided, default to true
          if (menuData?.hasOwnProperty('to_show')) {
            setIsFoodMenuVisible(menuData.to_show);
          } else {
            // Default to true if backend doesn't provide to_show yet
            setIsFoodMenuVisible(true);
          }
          
          // Set food menu data - keep the data object even if menu is empty
          // This allows us to show toggle when data was uploaded but hidden
          setFoodMenuData(menuData);
        }
      } else {
        setFoodMenuData(null);
        // Default to true if no data
        setIsFoodMenuVisible(true);
      }
    } catch (error) {
      console.error('Error fetching food menu:', error);
      setFoodMenuData(null);
    } finally {
      setIsLoadingFoodMenu(false);
    }
  };

  // Function to call getSessionAPI and map to agenda
  const callGetSessionAPI = async () => {
    if (!eventsDetail?._id) {
      return;
    }
    
    try {
      const response = await getEventSessionsAPI(eventsDetail._id);
      
      // Map sessions to agenda if response is successful
      if (response && !response.error && response.data && response.data.data) {
        const sessionsData = response.data.data;
        
        // Check if dates exist in the correct location
        if (sessionsData.data && sessionsData.data.dates && sessionsData.data.dates.length > 0) {
          const dates = sessionsData.data.dates;
          
          // Map each date's sessions to corresponding day's agenda
          dates.forEach((dateGroup, index) => {
            // Calculate the actual day number based on the date, not array index
            const calculateDayNumber = (dateString) => {
              if (eventsDetail?.from_date && dateString) {
                const eventStartDate = new Date(eventsDetail?.from_date);
                const currentDate = new Date(dateString);
                const timeDiff = currentDate.getTime() - eventStartDate.getTime();
                const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
                return dayDiff;
              }
              return index + 1; // Fallback to index-based calculation
            };
            
            const dayNumber = calculateDayNumber(dateGroup?.date || dateGroup?.dateString);
            const dayKey = `Day ${dayNumber}`;
            
            if (dateGroup.sessions && dateGroup.sessions.length > 0) {
              // Map sessions to agenda format
              const mappedAgenda = dateGroup.sessions.map(session => {
                // Process ALL speakers in the speaker_info array
                const speakers = session.speaker_info && session.speaker_info.length > 0 
                  ? session.speaker_info.map((speaker, index) => ({
                      id: Date.now() + Math.random() + index,
                      speaker_name: speaker.speaker_name || '',
                      speaker_designation: speaker.speaker_designation || '',
                      speaker_topic: speaker.speaker_topic || '',
                      speaker_details: speaker.speaker_details || '',
                      file_name: speaker.file_name || ''
                    }))
                  : []; // Empty array if no speaker_info - user needs to click add to add speakers
                
                const mappedItem = {
                  description: session.description || '',
                  end_time: session.end_time || '',
                  location: session.location || '',
                  session_id: session.session_id || '',
                  start_time: session.start_time || '',
                  title: session.title || '',
                  type: session.type || 'speaker',
                  speakers: speakers
                };
                return mappedItem;
              });
              
              // Update the specific day's agenda in defaultData
              setDefaultData(prevData => {
                // Create a deep copy of the data
                const updatedData = prevData.map(dayObj => {
                  const currentDayKey = Object.keys(dayObj)[0];
                  if (currentDayKey === dayKey) {
                    return {
                      [currentDayKey]: {
                        ...dayObj[currentDayKey],
                        agenda: mappedAgenda
                      }
                    };
                  }
                  return { ...dayObj };
                });
                
                return updatedData;
              });
            }
          });
        }
      }
    } catch (error) {
      console.error('Error calling getSessionAPI:', error);
    }
  };

  useEffect(() => {
    setNoOfDays();
    // setTabActiveButton("Day 1");
    setDefaultData(eventsDetail?.event_break_down);
    const container = containerRef.current;
    if (container) {
      setIsOverflowing(container.scrollWidth > container.clientWidth);
    }
    dateCalc();
    
    // Call getSessionAPI and log response
    callGetSessionAPI();
  }, [eventsDetail]);

  // Separate useEffect for food menu data to prevent duplicate calls
  useEffect(() => {
    if (eventsDetail?._id && tabActiveButton) {
      fetchFoodMenuData();
    }
  }, [tabActiveButton, eventsDetail?._id]);

  // Reset food menu fetch ref when event changes
  useEffect(() => {
    foodMenuFetchRef.current = null;
  }, [eventsDetail?._id]);

  useEffect(() => {
    setIsOpen(false);
  }, [tabActiveButton]);

  // Update selected day when defaultData changes and modal is open
  useEffect(() => {
    if (showModal && defaultData && defaultData.length > 0 && tabActiveButton) {
      const dayNumber = parseInt(tabActiveButton.split(' ')[1]);
      const dayData = defaultData[dayNumber - 1];
      if (dayData && dayData[tabActiveButton]) {
        setSelectedDay(dayData[tabActiveButton]);
      }
    }
  }, [defaultData, showModal, tabActiveButton]);

  const dateCalc = () => {
    const getDateChange = () => {
      const currentDate = new Date();
      const year = currentDate.getFullYear();
      const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
      const day = currentDate.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const curDate = getDateChange();
    const fromDate = new Date(eventsDetail.from_date);
    const toDate = new Date(eventsDetail.to_date);
    let checkDate = [];
    for (
      let currentDate = fromDate;
      currentDate <= toDate;
      currentDate.setDate(currentDate.getDate() + 1)
    ) {
      if (
        curDate === currentDate.toISOString().split('T')[0] ||
        curDate < currentDate.toISOString().split('T')[0]
      ) {
        checkDate.push(true);
      } else {
        checkDate.push(false);
      }
    }
    setCurrentDate(checkDate);
  };

  const setNoOfDays = () => {
    const fromDate = new Date(eventsDetail.from_date);
    const toDate = new Date(eventsDetail.to_date);
    const differenceInDays =
      Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1;
    setNoOfDayss(differenceInDays);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openModal = (action) => {
    if (action !== 'speaker_detail') scrollToTop();
    setShowModal(true);
    setForceRemount(!forceRemount);
  };

  const closeModal = () => {
    setShowModal(false);
    setForceRemount(!forceRemount);
  };

  // Access control functions
      const handleAddEventPlanClick = async () => {
      // Check if user is initiator/vendor and meets criteria
      const isInitiator = userInfo?.role === 'initiator';
      const isVendor = userInfo?.role === 'vendor';
      const isEventOwner = userInfo?.email === eventsDetail?.email;
      
      // For vendor: check if any agency has active status
      const isVendorActive = isVendor && eventsDetail?.agency && Array.isArray(eventsDetail?.agency) && 
        eventsDetail?.agency?.some(agency => agency?.status === 'active');
            
      // - Initiators who own the event (email matches)
      // - Vendors who have at least one active agency
      if ((isInitiator && isEventOwner) || (isVendor && isVendorActive)) {
      // First, get the latest user information to check current access status
      try {
        const eventAccessResponse = await getEventUserRequestStatusAPI(eventsDetail?._id, userInfo?.user_id);
        
        if (!eventAccessResponse.error && eventAccessResponse.data) {
          const accessStatus = eventAccessResponse.data?.data?.data?.event_request_status;

          if (accessStatus === 'ACCESS_NOT_REQUESTED') {
            scrollToTop();
            setAccessModalType('not_requested');
            setShowAccessModal(true);
          } else if (accessStatus === 'ACCESS_REQUESTED') {
            scrollToTop();
            setAccessModalType('requested');
            setShowAccessModal(true);
          } else if (accessStatus === 'ACCESS_REJECTED') {
            scrollToTop();
            setAccessModalType('rejected');
            setShowAccessModal(true);
          } else if (accessStatus === 'ACCESS_APPROVED') {
            // Normal flow - open create breakdown modal
            // Find the current day data
            const currentDayData = defaultData.find(day => Object.keys(day)[0] === tabActiveButton);
            if (currentDayData) {
              setSelectedDay(currentDayData[tabActiveButton]);
              setSelectedAction('create_breakdown');
              openModal();
            }
          }
        } else {
          toaster('Failed to check access status', 'error', 'error');
        }
      } catch (error) {
        console.error('Error fetching event access status:', error);
        toaster('Error checking access status', 'error', 'error');
      }
    } else {
      // For other users (different initiators/vendors or other roles), use existing flow
      // Find the current day data
      const currentDayData = defaultData.find(day => Object.keys(day)[0] === tabActiveButton);
      if (currentDayData) {
        setSelectedDay(currentDayData[tabActiveButton]);
        setSelectedAction('create_breakdown');
        openModal();
      }
    }
  };

  const handleRequestAccess = async () => {
    setIsRequestingAccess(true);
    LoaderAnimation.show();
    
    try {
      const response = await requestEventAccessAPI(eventsDetail?._id);
      if (response.error) {
        toaster(response.message || 'Failed to request access', 'error', 'error');
      } else {
        toaster(response?.data?.data?.message || 'Access request submitted successfully', 'success', 'success');
        setShowAccessModal(false);
      }
    } catch (error) {
      console.error('Error requesting access:', error);
      toaster('Error requesting access', 'error', 'error');
    } finally {
      setIsRequestingAccess(false);
      LoaderAnimation.hide();
    }
  };

  const closeAccessModal = () => {
    setShowAccessModal(false);
    setAccessModalType('');
  };

  // Delete session handlers
  const handleDeleteSession = (agendaItem, sessionIndex) => {
    scrollToTop();
    setSessionToDelete({ agendaItem, sessionIndex });
    setShowDeleteModal(true);
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete) return;
    
    try {
      setIsDeleting(true);
      LoaderAnimation.show();
      
      // Calculate the date based on event start date and current tab
      const calculateEventDate = () => {
        if (eventsDetail?.from_date && tabActiveButton) {
          const eventStartDate = new Date(eventsDetail.from_date);
          const dayNumber = parseInt(tabActiveButton.split(' ')[1]) || 1;
          const eventDate = new Date(eventStartDate);
          eventDate.setDate(eventStartDate.getDate() + (dayNumber - 1));
          return eventDate.toISOString().split('T')[0];
        }
        return new Date().toISOString().split('T')[0];
      };

      // Prepare delete data with required parameters
      const deleteData = {
        event_id: eventsDetail._id,
        date: calculateEventDate(),
        // start_time: sessionToDelete.agendaItem.start_time || '00:00',
        // end_time: sessionToDelete.agendaItem.end_time || '23:59',
        session_id: sessionToDelete.agendaItem.session_id || ''
      };
      
      const response = await deleteEventSessionAPI(deleteData);
      
      if (response.error) {
        toaster(
          response.message || 'Failed to delete session',
          'error',
          'error'
        );
        return;
      }
      
      // Show success message
      toaster(
        response.data?.data?.message || 
        'Session deleted successfully',
        'success',
        'success'
      );
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setSessionToDelete(null);
      
      // Refresh event details
      if (getEventDetails) {
        getEventDetails();
      }
      
    } catch (error) {
      console.error('Error deleting session:', error);
      toaster('Error deleting session', 'error', 'error');
    } finally {
      setIsDeleting(false);
      LoaderAnimation.hide();
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSessionToDelete(null);
  };

  // Delete modal components
  const getDeleteModalHeader = () => {
    return <Text>Delete Session</Text>;
  };

  const getDeleteModalBody = () => {
    return (
      <Box justifyContent={'center'} alignItems={'start'}>
        <Text
          fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
          fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
        >
          Are you sure you want to delete "{sessionToDelete?.agendaItem?.type}" session ?
        </Text>
      </Box>
    );
  };

  const getDeleteModalFooter = () => {
    return (
      <HStack justifyContent={'end'} width={'100%'} space={4}>
        <CustomButton
          onPress={closeDeleteModal}
          text='Cancel'
          color={COLORS.MODAL_BACK_BUTTON_COLOR}
          variant='outline'
        />
        <CustomButton
          onPress={confirmDeleteSession}
          text={isDeleting ? 'Deleting...' : 'Delete'}
          color={COLORS.WHITE_COLOR}
          backgroundColor={COLORS.REJECT_TEXT_COLOR}
          isLoading={isDeleting}
        />
      </HStack>
    );
  };



  // Function to update selected day data with latest event details
  const updateSelectedDayData = (updatedEventsDetail = null) => {
    const currentData = updatedEventsDetail || eventsDetail;

    if (currentData?.event_break_down && tabActiveButton) {
      const dayNumber = parseInt(tabActiveButton.split(' ')[1]);
      const dayData = currentData.event_break_down[dayNumber - 1];
      if (dayData && dayData[tabActiveButton]) {
        setSelectedDay(dayData[tabActiveButton]);
      }
    }
  };

  // Function to force update selected day from Redux store
  const forceUpdateSelectedDay = () => {
    // Use the current eventsDetail from props
    if (eventsDetail?.event_break_down && tabActiveButton) {
      const dayNumber = parseInt(tabActiveButton.split(' ')[1]);
      const dayData = eventsDetail.event_break_down[dayNumber - 1];
      if (dayData && dayData[tabActiveButton]) {
        setSelectedDay(dayData[tabActiveButton]);
      }
    }
  };

  // Update selected day when eventsDetail changes and modal is open
  useEffect(() => {
    if (showModal && eventsDetail?.event_break_down && tabActiveButton) {
      // Update selected day with latest data
      const dayNumber = parseInt(tabActiveButton.split(' ')[1]);
      const dayData = eventsDetail.event_break_down[dayNumber - 1];
      if (dayData && dayData[tabActiveButton]) {
        setSelectedDay(dayData[tabActiveButton]);
      }
    }
  }, [eventsDetail, showModal, tabActiveButton]);

  // Custom function to refresh event details and update selected day
  const refreshEventDetailsAndUpdateDay = async () => {
    try {
      // Call the event details API and get the response
      const response = await getEventDetails();

      // If we have a response, update the selected day immediately
      if (response && response.data && response.data.data && response.data.data.data) {
        const updatedEventsDetail = response.data.data.data;

        if (updatedEventsDetail?.event_break_down && tabActiveButton) {
          const dayNumber = parseInt(tabActiveButton.split(' ')[1]);
          const dayData = updatedEventsDetail.event_break_down[dayNumber - 1];
          if (dayData && dayData[tabActiveButton]) {
            setSelectedDay(dayData[tabActiveButton]);
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing event details:', error);
    }
  };

  const handleDaysCalculation = (data) => {
    const fromDate = new Date(eventsDetail.from_date);
    const toDate = new Date(eventsDetail.to_date);
    const differenceInDays =
      Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1;
    let options = [];
    let days = [];
    if (data && data.length > 0) {
      days = [...data];
    }
    for (var i = 0; i < differenceInDays; i++) {
      const key = `Day ${i + 1}`;
      {
        options.push(`Day ${i + 1}`);
      }
      days.push({
        [key]: {
          agenda: [
            {
              type: '',
              title: '',
              start_time: '',
              end_time: '',
              location: '',
              speaker_name: '',
              speaker_designation: '',
              speaker_topic: '',
              speaker_details: '',
              file_name: '',
              description: '',
            },
          ],
          menu: [{ title: '', list: [] }],
          status: '',
        },
      });
    }
    data && data.length === 0 && setDefaultData(days);
    if (
      tabActiveButton === 'Day 1' &&
      options.length > 0 &&
      options[0] !== 'Day 1'
    ) {
      setTabActiveButton(options[0]);
    }
    return options;
  };

  const getStartDateFormat = (dateString) => {
    const date = new Date(dateString);
    function padTo2Digits(num) {
      return num.toString().padStart(2, '0');
    }
    function formatDate(date) {
      return (
        [
          date.getFullYear(),
          padTo2Digits(date.getMonth() + 1),
          padTo2Digits(date.getDate()),
        ].join('-') +
        'T' +
        [
          padTo2Digits(date.getHours()),
          padTo2Digits(date.getMinutes()),
          padTo2Digits(date.getSeconds()),
        ].join(':') +
        '.' +
        '616838+00:00'
      );
    }
    const formattedDate = formatDate(date);
    return formattedDate;
  };

  const uploadFilesToServer = async (data, type, upload_type) => {
    let docs = [];
    let res = await uploadFileAPI(data, type, upload_type);
    res.map((item) => {
      if (item.error) {
        toaster('Upload file failed', 'error', 'error');
        return;
      }
      if (item.data.data.data) {
        docs.push({
          doc_type: item.data.data.data.doc_type,
          file_size: item.data.data.data.file_size.toString(),
          file_name: item.data.data.data.file_name,
          uploaded_time: getStartDateFormat(new Date()),
        });
      }
    });
    return docs;
  };

  const handleEventCreation = async () => {
    LoaderAnimation.show();
    let submittedData = childRef.current.onSubmitInput();
    
    // Check if validation failed
    if (submittedData === false) {
      LoaderAnimation.hide();
      toaster('Please enter proper data', 'error', 'error');
      return;
    }
    
    let menu = childref?.current?.handleFoodMenu();
    let agenda = agendaRef?.current?.handleFoodMenu();
    let data = { ...submittedData },
      flag = true;
    data['menu'] = menu;
    data['agenda'] = agenda;
    if (data['menu']?.length === 0) {
      data['menu'] = [{ title: '', list: [] }];
    }
    if (data['agenda']?.length === 0) {
      data['agenda'] = [{ title: '', list: [] }];
    }
    let final,
      remainSame = false,
      index;
    let bool = false;
    data['agenda']?.forEach((val) => {
      val.list?.forEach((listString) => {
        if (listString > 20000) {
          bool = true;
        }
      });
    });
    if (bool) {
      toaster('Enter Agenda less than 20000 characters', 'error', 'error');
      LoaderAnimation.hide();
      flag = false;
    }
    if (
      data?.menu?.every(
        (obj) =>
          obj.list.length > 0 &&
          obj.list.every(
            (value) =>
              !/^(?!\s)[^<>",`{}\\]*$/.test(value) ||
              /https?:\/\/\S+\b/i.test(value) ||
              /www\.\S+\b/i.test(value) ||
              /http:\/\/.+\b/i.test(value)
          )
      ) ||
      data?.menu?.every(
        (obj) =>
          (obj.title !== '' && !/^(?!\s)[^<>",`{}\\]*$/.test(obj.title)) ||
          /https:\/\/.+\b/i.test(obj.title) ||
          /http:\/\/.+\b/i.test(obj.title) ||
          /www\.\S+\b/i.test(obj.title)
      )
    ) {
      toaster('Enter valid Food Menu', 'error', 'error');
      LoaderAnimation.hide();
      flag = false;
    }
    if (
      data?.agenda?.every(
        (obj) =>
          obj?.list?.length > 0 &&
          obj?.list?.every(
            (value) =>
              !/^(?!\s)[^<>",`{}\\]*$/.test(value) ||
              /https?:\/\/\S+\b/i.test(value) ||
              /www\.\S+\b/i.test(value) ||
              /http:\/\/.+\b/i.test(value)
          )
      ) ||
      data?.agenda?.every(
        (obj) =>
          (obj.title !== '' && !/^(?!\s)[^<>",`{}\\]*$/.test(obj.title)) ||
          /https:\/\/.+\b/i.test(obj.title) ||
          /http:\/\/.+\b/i.test(obj.title) ||
          /www\.\S+\b/i.test(obj.title)
      )
    ) {
      toaster('Enter valid Food agenda', 'error', 'error');
      LoaderAnimation.hide();
      flag = false;
    }
    if (
      data?.agenda?.length === 0 &&
      data?.menu?.every(
        (obj) =>
          (obj.list.length === 0 ||
            obj.list.every((value) => value === '')) &&
          obj.title === ''
      )
    ) {
      setSelectedDay({
        agenda: [
          {
            type: '',
            title: '',
            start_time: '',
            end_time: '',
            location: '',
            speaker_name: '',
            speaker_designation: '',
            speaker_topic: '',
            speaker_details: '',
            file_name: '',
            description: '',
          },
        ],
        menu: [{ title: '', list: [] }],
        status: '',
      });
      const updatedData = defaultData.map((item) => {
        if (item.hasOwnProperty(tabActiveButton)) {
          let res = { ...item };
          res[tabActiveButton] = {
            agenda: [
              {
                type: '',
                title: '',
                start_time: '',
                end_time: '',
                location: '',
                speaker_name: '',
                speaker_designation: '',
                speaker_topic: '',
                speaker_details: '',
                file_name: '',
                description: '',
              },
            ],
            menu: [{ title: '', list: [] }],
            status: '',
          };
          return res;
        }
        return item;
      });
      setDefaultData(updatedData);
      final = updatedData;
      index = -1;
      remainSame = true;
    } else {
      // Update only the selected day's data with submittedData, others remain as is
      final = defaultData.map((item) => {
        const dayKey = Object.keys(item)[0];
        if (dayKey === tabActiveButton) {
          return {
            [dayKey]: {
              ...submittedData,
              status: selectedAction === 'create_breakdown' ? 'added' : item[dayKey].status || '',
            },
          };
        }
        return item;
      });
      index = final.findIndex((item) => Object.keys(item)[0] === tabActiveButton);
    }
    setDefaultData(final);
    let payload = { ...eventsDetail };
    payload['workflow_instance_id'] = payload['_id'];
    payload['event_break_down'] = final;
    delete payload['_id'];
    let result = { instance: [payload] };
    
    // Calculate the correct date based on event start date and selected day
    const calculateEventDate = () => {
      if (eventsDetail?.from_date && tabActiveButton) {
        const eventStartDate = new Date(eventsDetail.from_date);
        const dayNumber = parseInt(tabActiveButton.split(' ')[1]) || 1;
        const eventDate = new Date(eventStartDate);
        eventDate.setDate(eventStartDate.getDate() + (dayNumber - 1));
        return eventDate.toISOString().split('T')[0];
      }
      return new Date().toISOString().split('T')[0];
    };

    // Add the calculated date to the submitted data
    const payloadWithDate = {
      ...submittedData,
      date: calculateEventDate()
    };

    if (selectedAction === 'create_breakdown' && !remainSame) {
      // For creation, ensure session_id is not included in the payload
      const createPayload = {
        date: payloadWithDate.date,
        sessions: payloadWithDate.sessions ? payloadWithDate.sessions.map(session => {
          // Remove session_id for creation
          const { session_id, ...sessionWithoutId } = session;
          return sessionWithoutId;
        }) : []
      };
      
      // Use the new event session API with the new payload structure
      let res = await addEventSessionAPI(eventsDetail?._id, createPayload);
      if (res.error) {
        toaster(res.message, 'error', 'error');
        LoaderAnimation.hide();
        return;
      } else {
        toaster(
          res.data?.data?.message || 
          `Event Plan for ${tabActiveButton} Added Successfully`,
          'success',
          'success'
        );
      }
    } else if (selectedAction === 'edit_breakdown') {
      const editPayloadWithSessionId = {
        date: payloadWithDate.date,
        sessions: payloadWithDate.sessions || []
      };
      
      // Use the new event session API for editing as well
      let res = await editEventSessionAPI(eventsDetail?._id, editPayloadWithSessionId);
      if (res.error) {
        toaster(res.message, 'error', 'error');
        LoaderAnimation.hide();
        return;
      } else {
        toaster(
          res.data?.data?.message || 
          `Event plan for ${tabActiveButton} updated Successfully`,
          'success',
          'success'
        );
      }
    }
    getEventDetails();
    LoaderAnimation.hide();
    closeModal();
  };

  const modalHeader = () => {
    if (
      selectedAction === 'create_breakdown' ||
      selectedAction === 'edit_breakdown'
    ) {
      return <Text>{eventTab} Itinerary </Text>;
    } else if (selectedAction === 'speaker_detail') {
      return <Text>Speaker Detail</Text>;
    }
  };

  const modalChildren = () => {
    if (selectedAction === 'create_breakdown') {
      return (
        <CreateEventBreakDown
          eventTab={eventTab}
          defaultValue={selectedDay}
          ref={childRef}
          childref={childref}
          agendaRef={agendaRef}
          eventId={eventsDetail?._id}
          type='create'
          setSelectedDay={setSelectedDay}
          forceRemount={forceRemount}
          setForceRemount={setForceRemount}
          setEventBreakError={setEventBreakError}
          getEventDetails={refreshEventDetailsAndUpdateDay}
          currentTab={tabActiveButton}
          updateSelectedDay={updateSelectedDayData}
          dateOfEvent={(() => {
            if (eventsDetail?.from_date && tabActiveButton) {
              const eventStartDate = new Date(eventsDetail.from_date);
              const dayNumber = parseInt(tabActiveButton.split(' ')[1]) || 1;
              const eventDate = new Date(eventStartDate);
              eventDate.setDate(eventStartDate.getDate() + (dayNumber - 1));
              return eventDate.toISOString().split('T')[0];
            }
            return null;
          })()}
        />
      );
    } else if (selectedAction === 'edit_breakdown') {
      return (
        <CreateEventBreakDown
          eventTab={eventTab}
          defaultValue={selectedDay}
          ref={childRef}
          childref={childref}
          agendaRef={agendaRef}
          eventId={eventsDetail?._id}
          type='edit'
          forceRemount={forceRemount}
          setForceRemount={setForceRemount}
          setEventBreakError={setEventBreakError}
          getEventDetails={refreshEventDetailsAndUpdateDay}
          currentTab={tabActiveButton}
          updateSelectedDay={updateSelectedDayData}
          dateOfEvent={(() => {
            if (eventsDetail?.from_date && tabActiveButton) {
              const eventStartDate = new Date(eventsDetail.from_date);
              const dayNumber = parseInt(tabActiveButton.split(' ')[1]) || 1;
              const eventDate = new Date(eventStartDate);
              eventDate.setDate(eventStartDate.getDate() + (dayNumber - 1));
              return eventDate.toISOString().split('T')[0];
            }
            return null;
          })()}
        />
      );
    } else if (selectedAction === 'speaker_detail') {
      return !MobileWindow ? (
        <>
          <EventPlanDetailsCards
            mt='3px'
            body={
              <HStack
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  margin: '14px',
                  gap: '15px',
                }}
                width={'95%'}
              >
                <Box>
                  {selectedSpeaker?.file_name && (
                    <Avatar
                      source={{
                        uri: config.bucketPath + selectedSpeaker?.file_name,
                      }}
                      size='xl'
                    />
                  )}
                  {selectedSpeaker?.file_name === '' && (
                    <Avatar bg={'#d9d9d9'} size={'xl'}>
                      {speakerNoPersonSVG}
                    </Avatar>
                  )}
                </Box>
                <VStack space={2} justifyContent={'center'} width={'40%'}>
                  <Text
                    color={COLORS.BLACK_COLOR}
                    fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                    fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                  >
                    {selectedSpeaker.speaker_name}
                  </Text>
                  <Text color={COLORS.ATTACHMENTS_TEXT_COLOR}>
                    {selectedSpeaker.speaker_designation}
                  </Text>
                </VStack>
                {(selectedSpeaker.start_time || selectedSpeaker.end_time) && (
                  <>
                    <DividerLine height='auto' />
                    <VStack space={1} justifyContent={'center'}>
                      <Text
                        color={COLORS.ATTACHMENTS_TEXT_COLOR}
                        fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE}
                      >
                        Time
                      </Text>
                      <Text
                        color={COLORS.BLACK_COLOR}
                        fontSize={'24px'}
                        fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                      >
                        {selectedSpeaker.start_time && convertTo12HourFormat(selectedSpeaker.start_time)}
                        {selectedSpeaker.start_time && selectedSpeaker.end_time && ' - '}
                        {selectedSpeaker.end_time && convertTo12HourFormat(selectedSpeaker.end_time)}
                      </Text>
                    </VStack>
                  </>
                )}
              </HStack>
            }
          />
          {selectedSpeaker.speaker_details && (
            <>
              <Text
                color={COLORS.BLACK_COLOR}
                fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                mt={'24px'}
              >
                About Speaker
              </Text>
              <Text color={COLORS.ATTACHMENTS_TEXT_COLOR}>
                {selectedSpeaker.speaker_details}
              </Text>
            </>
          )}
          {selectedSpeaker.speaker_topic && (
            <>
              <Text
                color={COLORS.BLACK_COLOR}
                fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                mt={'24px'}
              >
                Speaker Topic
              </Text>
              <Text color={COLORS.ATTACHMENTS_TEXT_COLOR}>
                {selectedSpeaker.speaker_topic}
              </Text>
            </>
          )}
        </>
      ) : (
        <>
          <EventPlanDetailsCards
            body={
              <HStack
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '15px',
                }}
                width={'100%'}
                mx={'12px'}
                mb={'16px'}
                mt={'6px'}
              >
                <Box width={'10%'}>
                  {selectedSpeaker?.file_name && (
                    <Avatar
                      source={{
                        uri: config.bucketPath + selectedSpeaker?.file_name,
                      }}
                      size='md'
                    />
                  )}
                  {selectedSpeaker?.file_name === '' && (
                    <Avatar bg={'#d9d9d9'} size={'md'}>
                      {speakerNoPersonSVG}
                    </Avatar>
                  )}
                </Box>
                <VStack width={'70%'}>
                  <VStack justifyContent={'center'} width={'100%'}>
                    <Text
                      color={COLORS.BLACK_COLOR}
                      fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                      fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                    >
                      {selectedSpeaker.speaker_name}
                    </Text>
                    <Text
                      color={COLORS.ATTACHMENTS_TEXT_COLOR}
                      fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE}
                    >
                      {selectedSpeaker.speaker_designation}
                    </Text>
                  </VStack>
                  <HStack
                    space={1}
                    justifyContent={'space-between'}
                    width={'100%'}
                    mt={2}
                  >
                    <HStack>
                      <Text
                        color={COLORS.ATTACHMENTS_TEXT_COLOR}
                        fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE}
                      >
                        Time:{' '}
                      </Text>
                      <Text
                        color={COLORS.BLACK_COLOR}
                        fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                        fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                      >
                        {selectedSpeaker.start_time && convertTo12HourFormat(selectedSpeaker.start_time)}
                        {selectedSpeaker.start_time && selectedSpeaker.end_time && ' - '}
                        {selectedSpeaker.end_time && convertTo12HourFormat(selectedSpeaker.end_time)}
                      </Text>
                    </HStack>
                  </HStack>
                </VStack>
              </HStack>
            }
          />
          {selectedSpeaker.speaker_details && (
            <>
              <Text
                color={COLORS.BLACK_COLOR}
                fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                mt={'24px'}
              >
                About Speaker
              </Text>
              <Text color={COLORS.ATTACHMENTS_TEXT_COLOR}>
                {selectedSpeaker.speaker_details}
              </Text>
            </>
          )}
          {selectedSpeaker.speaker_topic && (
            <>
              <Text
                color={COLORS.BLACK_COLOR}
                fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                mt={'24px'}
              >
                Speaker Topic
              </Text>
              <Text color={COLORS.ATTACHMENTS_TEXT_COLOR}>
                {selectedSpeaker.speaker_topic}
              </Text>
            </>
          )}
        </>
      );
    }
  };

  const modalFooter = () => {
    if (selectedAction === 'create_breakdown') {
      return (
        <Box width={MobileWindow && '100%'}>
          <CustomButton
            onPress={() => {
              handleEventCreation();
            }}
            // isDisabled={eventBreakError && true}
            text='Create'
            color={COLORS.WHITE_COLOR}
          />
        </Box>
      );
    } else if (selectedAction === 'edit_breakdown') {
      return (
        <Box width={MobileWindow && '100%'}>
          <CustomButton
            onPress={() => {
              handleEventCreation();
            }}
            // isDisabled={eventBreakError && true}
            text='Update'
            color={COLORS.WHITE_COLOR}
          />
        </Box>
      );
    }
  };

  // Access control modal components
  const getAccessModalHeader = () => {
    switch (accessModalType) {
      case 'not_requested':
        return <Text>Access Required</Text>;
      case 'requested':
        return <Text>Access Request Pending</Text>;
      case 'rejected':
        return <Text>Access Denied</Text>;
      default:
        return <Text>Access</Text>;
    }
  };

  const getAccessModalBody = () => {
    switch (accessModalType) {
      case 'not_requested':
        return (
          <VStack space={4} alignItems="center" p={4}>
            {noPhotosUploadedSVG}
            <Text
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
              color={COLORS.BLACK_COLOR}
              textAlign="center"
            >
              Access is not provided to you, kindly raise a request to access
            </Text>
          </VStack>
        );
      case 'requested':
        return (
          <VStack space={4} alignItems="center" p={4}>
            {noPhotosUploadedSVG}
            <Text
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
              color={COLORS.BLACK_COLOR}
              textAlign="center"
            >
              Already raised request with the admin & pending for the approval
            </Text>
          </VStack>
        );
      case 'rejected':
        return (
          <VStack space={4} alignItems="center" p={4}>
            {noPhotosUploadedSVG}
            <Text
              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
              fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
              color={COLORS.BLACK_COLOR}
              textAlign="center"
            >
              Your access to request have been rejected
            </Text>
          </VStack>
        );
      default:
        return null;
    }
  };

  const getAccessModalFooter = () => {
    switch (accessModalType) {
      case 'not_requested':
        return (
          <HStack space={3} justifyContent="end" width="100%">
            <CustomButton
              text="Cancel"
              onPress={closeAccessModal}
              isOutline
              bgColor={COLORS.BLACK_COLOR}
              textColor={COLORS.BLACK_COLOR}
            />
            <CustomButton
              text="Request Access"
              onPress={handleRequestAccess}
              isDisabled={isRequestingAccess}
              bgColor={COLORS.THEME_COLOR}
              textColor={COLORS.WHITE_COLOR}
            />
          </HStack>
        );
      case 'requested':
      case 'rejected':
        return (
          <Box width="100%" alignItems="center">
            <CustomButton
              text="Close"
              onPress={closeAccessModal}
              bgColor={COLORS.THEME_COLOR}
              textColor={COLORS.WHITE_COLOR}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  const handleKnowMore = (item) => {
    setSelectedAction('speaker_detail');
    setSelectedSpeaker(item);
    openModal('speaker_detail');
  };

  const activeButton = (val) => {
    // setTabActiveButton("");
    let day = val.split(' ')[1];
    setSelectedDay(eventsDetail.event_break_down[day - 1][val]);
    setTabActive(val);
    setTabActiveButton(val);
    // Food menu data will be fetched automatically by useEffect when tabActiveButton changes
  };

  function convertTo12HourFormat(time24) {
    if (time24) {
      var [hours, minutes] = time24.split(':');
      var suffix = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12 || 12;
      return hours + ':' + minutes + ' ' + suffix;
    }
  }

  // Helper function to sort agenda items by time
  const sortAgendaByTime = (agenda) => {
    return agenda.sort((a, b) => {
      const timeA = a.start_time || a.end_time || '';
      const timeB = b.start_time || b.end_time || '';

      if (!timeA && !timeB) return 0;
      if (!timeA) return 1;
      if (!timeB) return -1;

      return timeA.localeCompare(timeB);
    });
  };

  // Helper function to render agenda items with section titles
  const renderAgendaWithSections = (agenda) => {
    const sortedAgenda = sortAgendaByTime([...agenda]);
    // console.log('sortedAgenda', sortedAgenda);
    return sortedAgenda.map((agendaItem, index) => (
      <Box key={index} mb={index > 0 ? '35px' : '16px'}>
        <HStack
          width={'100%'}
          justifyContent={'space-between'}
          alignItems={'center'}
          mb={4}
        >
          <Text
            fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
            fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
          >
            {agendaItem.type || 'Agenda Details'}
          </Text>
          <Pressable onPress={() => handleDeleteSession(agendaItem, index)} 
            mr={4}>
            <Svg
              width={'20'}
              height={'20'}
              viewBox={`0 0 24 24`}
            >
              {deleteCardsIconSVG}
            </Svg>
          </Pressable>
        </HStack>
        {renderAgendaItem(agendaItem, index)}
      </Box>
    ));
  };

  // Helper function to get speaker data from agenda item
  const getSpeakerData = (agendaItem) => {
    // Check new data structure first (speakers array)
    if (agendaItem.speakers && agendaItem.speakers.length > 0 && agendaItem.speakers[0].speaker_name) {
      return agendaItem.speakers[0];
    }
    // Fallback to old data structure (direct properties)
    return {
      speaker_name: agendaItem.speaker_name || '',
      speaker_designation: agendaItem.speaker_designation || '',
      speaker_topic: agendaItem.speaker_topic || '',
      speaker_details: agendaItem.speaker_details || '',
      file_name: agendaItem.file_name || ''
    };
  };

  // Helper function to render agenda item based on type
  const renderAgendaItem = (agendaItem, index) => {
    // Check if this item has speaker information - check both old and new data structures
    const hasSpeakerInfo = (agendaItem.speaker_name && agendaItem.speaker_name.trim() !== '') || 
                          (agendaItem.speakers && agendaItem.speakers.length > 0 && 
                           agendaItem.speakers[0].speaker_name && agendaItem.speakers[0].speaker_name.trim() !== '');
    
     if (hasSpeakerInfo) {
       // Get all speakers instead of just the first one
       const allSpeakers = agendaItem.speakers && agendaItem.speakers.length > 0 
         ? agendaItem.speakers 
         : [getSpeakerData(agendaItem)]; // Fallback to old structure
       
       return (
         <VStack space={3} key={index}>
           {/* Common Details Card */}
           <EventPlanDetailsCards
             body={
               <HStack p={5} space={3} width={'70%'}>
                 <VStack justifyContent={'flex-start'} width={'100%'} alignItems={'flex-start'} space={3}>
                   <Text
                     fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                     fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                   >
                     Title : {agendaItem.title}
                   </Text>
                   {agendaItem.location && (
                     <Text color={COLORS.ATTACHMENTS_TEXT_COLOR}>
                       Location : {agendaItem.location}
                     </Text>
                   )}
                   {agendaItem.description && (
                     <Text color={COLORS.ATTACHMENTS_TEXT_COLOR}>
                       Description : {agendaItem.description}
                     </Text>
                   )}
                 </VStack>
                 {(agendaItem.start_time || agendaItem.end_time) && (
                   <>
                     <DividerLine height='auto' />
                     <VStack space={1} justifyContent={'center'}>
                       <Text
                         color={COLORS.ATTACHMENTS_TEXT_COLOR}
                         fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE}
                       >
                         Time
                       </Text>
                       <Text
                         color={COLORS.BLACK_COLOR}
                         fontSize={'24px'}
                         fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                         textAlign={'right'}
                       >
                         {agendaItem.start_time && convertTo12HourFormat(agendaItem.start_time)}
                         {agendaItem.start_time && agendaItem.end_time && ' - '}
                         {agendaItem.end_time && convertTo12HourFormat(agendaItem.end_time)}
                       </Text>
                     </VStack>
                   </>
                 )}
               </HStack>
             }
           />
           
           {/* Speaker Details Card - Display ALL speakers */}
           <VStack space={2} >
             <Text
               fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
               fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
               color={COLORS.BLACK_COLOR}
               mb={2}
             >
               Speaker Details
             </Text>
             {allSpeakers.map((speaker, speakerIndex) => (
               <EventPlanDetailsCards
                 key={speakerIndex}
                 body={
                   <HStack
                     style={{
                       display: 'flex',
                       flexWrap: 'wrap',
                       margin: '14px',
                       gap: '15px',
                     }}
                     width={'97%'}
                     justifyContent={'space-between'}
                     p={2}
                   >
                     <HStack space={4} width={'70%'}>
                       <Box>
                         {speaker?.file_name && (
                           <Avatar
                             source={{
                               uri: config.bucketPath + speaker?.file_name,
                             }}
                             size='xl'
                           />
                         )}
                         {!speaker?.file_name && (
                           <Avatar bg={'#d9d9d9'} size={'xl'}>
                             {speakerNoPersonSVG}
                           </Avatar>
                         )}
                       </Box>
                       <VStack
                         space={2}
                         justifyContent={'center'}
                         width={'70%'}
                       >
                         <Text
                           color={COLORS.BLACK_COLOR}
                           fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
                           fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                         >
                           {speaker.speaker_name}
                         </Text>
                         <Text 
                           color={COLORS.ATTACHMENTS_TEXT_COLOR}
                           fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                         >
                           {speaker.speaker_designation}
                         </Text>
                       </VStack>
                     </HStack>
                     <Pressable
                       onPress={() => {
                         setSelectedAction('speaker_detail');
                         // Combine speaker data with session time data
                         const speakerWithTime = {
                           ...speaker,
                           start_time: agendaItem.start_time || '',
                           end_time: agendaItem.end_time || ''
                         };
                         setSelectedSpeaker(speakerWithTime);
                         openModal('speaker_detail');
                       }}
                       justifyContent={'start'}
                     >
                       <Text
                         color={COLORS.TOGGLE_ACTIVE_BUTTON_COLOR}
                         fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                       >
                         Know More
                       </Text>
                     </Pressable>
                   </HStack>
                 }
               />
             ))}
           </VStack>
         </VStack>
       );
    } else {
      // Render as general agenda item for any other type - matching the card style from images
      return (
        <EventPlanDetailsCards
            body={
            <HStack 
              p={4} 
              space={4} 
              width={'70%'}
              borderRadius={'8px'}
              justifyContent={'space-between'}
            >
              <VStack justifyContent={'space-between'} width={'100%'} alignItems={'baseline'} space={2}>
                <Text
                  fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                  fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                  color={COLORS.BLACK_COLOR}
                >
                  Title : {agendaItem.title}
                </Text>
                {agendaItem.location && (
                  <Text 
                    color={COLORS.ATTACHMENTS_TEXT_COLOR}
                    fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                  >
                    Location : {agendaItem.location}
                  </Text>
                )}
                {agendaItem.description && (
                  <Text 
                    color={COLORS.ATTACHMENTS_TEXT_COLOR}
                    fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                  >
                    Description : {agendaItem.description}
                  </Text>
                )}
              </VStack>
              {(agendaItem.start_time || agendaItem.end_time) && (
                <>
                  <DividerLine height='auto' />
                  <VStack space={1} justifyContent={'center'}>
                    <Text
                      color={COLORS.ATTACHMENTS_TEXT_COLOR}
                      fontSize={COMMONFONTSIZE.SMALL_FONT_SIZE}
                    >
                      Time
                    </Text>
                    <Text
                      color={COLORS.BLACK_COLOR}
                      fontSize={'24px'}
                      fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                    >
                      {agendaItem.start_time && convertTo12HourFormat(agendaItem.start_time)}
                      {agendaItem.start_time && agendaItem.end_time && ' - '}
                      {agendaItem.end_time && convertTo12HourFormat(agendaItem.end_time)}
                    </Text>
                  </VStack>
                </>
              )}
            </HStack>
          }
        />
      );
    }
  };

  return (
    <VStack width={'100%'}>
      <CommonHeading text={`${eventTab} Breakdown`} />
      {eventsDetail.from_date !== eventsDetail.to_date &&
        handleDaysCalculation(eventsDetail?.event_break_down).length !== 0 && (
          <Box ref={containerRef} mt={5} style={{ overflow: 'hidden' }}>
            <ToggleButton
              options={handleDaysCalculation(eventsDetail?.event_break_down)}
              activeButton={tabActiveButton}
              setActiveButton={activeButton}
              width={!MobileWindow && (isOverflowing ? 'auto' : 'fit-content')}
            />
          </Box>
        )}
      <VStack
        alignItems={'center'}
        space={4}
        bg={!MobileWindow && COLORS.WHITE_COLOR}
        borderRadius={'8px'}
        style={{
          boxShadow: '0px 18.953px 47.382px 0px rgba(119, 115, 170, 0.10);',
        }}
        mt={
          eventsDetail.from_date === eventsDetail.to_date
            ? !MobileWindow
              ? '27px'
              : '0'
            : !MobileWindow
              ? '10px'
              : '0px'
        }
        pt={
          !MobileWindow
            ? '24px'
            : eventsDetail.from_date === eventsDetail.to_date
              ? '0'
              : !MobileWindow
                ? '10px'
                : '0px'
        }
        pb={!MobileWindow && '12px'}
        width={'100%'}
      >
        {defaultData &&
          defaultData.map((item, ind) => {
            if (Object.keys(item)[0] === tabActiveButton) {
              // Check if there's actual data (agenda or menu) regardless of status
              const agendaData = item[tabActiveButton]?.agenda;
              
              const hasAgendaData = agendaData &&
                agendaData.length > 0 &&
                agendaData.some(agenda => {
                  const hasContent = agenda.title || agenda.speaker_name || agenda.type || agenda.description || agenda.location;
                  return hasContent;
                });
              
              const hasMenuData = item[tabActiveButton]?.menu &&
                item[tabActiveButton]?.menu?.some(menu =>
                  menu.title || (menu.list && menu.list.length > 0 && menu.list.some(item => item !== ''))
                );

              const hasData = hasAgendaData || hasMenuData;

              // If there's data, show it regardless of status
              if (hasData) {
                return !MobileWindow ? (
                  <Box px={'24px'} width={'100%'} position={'relative'}>
                    {currentDate[ind] &&
                      ((
                        hasPermission('ADD_EVENT_BREAKDOWN')) ||
                        (hasPermission('VENDOR') && selected)) &&
                      statusMap.indexOf(eventsDetail.status) < 3 && (
                        <Box
                          alignItems={'end'}
                          width={'95%'}
                          position={'absolute'}
                          style={{
                            transform: [{ translateX: 4 }, { translateY: -13 }],
                          }}
                          zIndex={'999'}
                        >
                          <Pressable
                            onPress={() => {
                              // Use the updated data from defaultData instead of the original item
                              const currentDayData = defaultData.find(day => Object.keys(day)[0] === tabActiveButton);
                              
                              if (currentDayData) {
                                setSelectedDay(currentDayData[tabActiveButton]);
                              } else {
                                setSelectedDay(item[tabActiveButton]);
                              }
                              setSelectedAction('edit_breakdown');
                              openModal();
                            }}
                          >
                            {editIconDetailsPage}
                          </Pressable>
                        </Box>
                      )}
                    {item &&
                      item[tabActiveButton] &&
                      item[tabActiveButton]?.agenda &&
                      item[tabActiveButton]?.agenda?.length > 0 && (
                        <Box mb={'35px'}>
                          <Text
                            fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
                            fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                            mb={4}
                            color={COLORS.BLACK_COLOR}
                          >
                            Agenda Details
                          </Text>
                          {renderAgendaWithSections(item[tabActiveButton]?.agenda)}
                        </Box>
                      )}
                    
                    {/* Display Food Menu from API */}
                    <Box mb={'35px'}>
                      <HStack
                        alignItems={'center'}
                        justifyContent={'space-between'}
                        width={'100%'}
                        mb={4}
                      >
                        <HStack alignItems={'center'} space={3}>
                          <Text
                            fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
                            fontWeight={
                              COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT
                            }
                          >
                            Food Menu
                          </Text>
                          {foodMenuData && foodMenuData?.uploaded_at && (
                            <ToggleSwitch
                              isOn={isFoodMenuVisible}
                              onToggle={() => handleFoodMenuToggle(!isFoodMenuVisible)}
                            />
                          )}
                        </HStack>
                        <Pressable onPress={() => {
                          scrollToTop();
                          setShowBulkUploadModal(true);
                        }}>
                          <Text
                            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                            color={COLORS.TABS_ACTIVE_BORDER_COLOR}
                            fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                          >
                            Bulk Upload
                          </Text>
                        </Pressable>
                      </HStack>
                      
                      {/* Display Food Menu from API */}
                      {!isFoodMenuVisible ? (
                        <Box mt={4}>
                          <Text
                            fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                            fontSize={"20px"}
                            color={COLORS.ACCORDION_TITLE_COLOR}
                            textAlign={"center"}
                          >
                            Food Menu is hidden. Toggle On to see the results
                          </Text>
                        </Box>
                      ) : isLoadingFoodMenu ? (
                        <EventPlanDetailsCards
                          mt='3px'
                          body={
                            <VStack
                              p={5}
                              alignItems="center"
                              justifyContent="center"
                              minHeight="100px"
                            >
                              <Text
                                fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                                color={COLORS.ATTACHMENTS_TEXT_COLOR}
                              >
                                Loading food menu...
                              </Text>
                            </VStack>
                          }
                        />
                      ) : foodMenuData ? (
                        <Box mt={4}>
                          <FoodMenuCard menuData={foodMenuData} />
                        </Box>
                      ) : (
                        <Box mt={4}>
                          <NoDataFound 
                            customMessage={true}
                            dataName="food menu data"
                            image={false}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <VStack space={'16px'} width={'100%'} mt={4}>
                    {headings.map((head, ind) => {
                      if (
                        (head.value === 'agenda' &&
                          item[tabActiveButton][head.value]) ||
                        (head.value === 'menu' &&
                          item[tabActiveButton][head.value].some(
                            (obj) =>
                              (Array.isArray(obj.list) && obj.list.length > 0 &&
                                obj.list.some((value) => value !== '')) ||
                              obj.title !== ''
                          ))
                      )
                        return (
                          <AccordionExample
                            tabActiveButton={tabActiveButton}
                            item={item}
                            headings={head}
                            index={ind}
                            handleKnowMore={handleKnowMore}
                          />
                        );
                    })}
                  </VStack>
                );
              }

              // If no data, show appropriate message based on permissions and date
              if (item[tabActiveButton].status !== 'added') {
                if (
                  (hasPermission('ADD_EVENT_BREAKDOWN')) ||
                  (hasPermission('VENDOR') && selected)
                ) {
                  if (statusMap.indexOf(eventsDetail.status) < 4)
                    return currentDate[ind] && !MobileWindow ? (
                      <>
                        {noPhotosUploadedSVG}
                        <Box alignItems={'center'} mx={MobileWindow && 8}>
                          <Text
                            fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                            fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                            color={COLORS.BLACK_COLOR}
                            textAlign={MobileWindow && 'center'}
                          >
                            You have not created any{' '}
                            {eventTab === 'Event' ? 'event' : 'MICE'} plan yet,
                            Please create it
                          </Text>
                        </Box>
                        {!MobileWindow && (
                          <CustomButton
                            bgColor={COLORS.THEME_COLOR}
                            borderRadius='50px'
                            text={`Add ${eventTab} Plan`}
                            textColor={COLORS.WHITE_COLOR}
                            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                            fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
                            width='auto'
                            mt={1}
                            py={2}
                            onPress={handleAddEventPlanClick}
                          />
                        )}
                      </>
                    ) : (
                      <Box
                        bg={MobileWindow && COLORS.WHITE_COLOR}
                        borderRadius={'8px'}
                        style={
                          MobileWindow && {
                            boxShadow:
                              '0px 18.953px 47.382px 0px rgba(119, 115, 170, 0.10);',
                          }
                        }
                        mt={
                          MobileWindow &&
                          (eventsDetail.from_date === eventsDetail.to_date
                            ? '10px'
                            : '10px')
                        }
                        pb={MobileWindow && 3}
                        alignItems={MobileWindow && 'center'}
                        width={'100%'}
                      >
                        <Box margin={'auto'} alignItems={'center'}>
                          {noPhotosUploadedSVG}
                        </Box>
                        <Box alignItems={'center'}>
                          <Text
                            fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                            fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                            color={COLORS.BLACK_COLOR}
                          >
                            No {eventTab === 'Event' ? 'event' : 'MICE'} plan
                            available
                          </Text>
                        </Box>
                        {/* {noPhotosUploadedSVG}
                          <Box alignItems={"center"} mx={MobileWindow && 8}>
                            <Text
                              fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                              fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                              color={COLORS.BLACK_COLOR}
                              textAlign={MobileWindow && "center"}
                            >
                              You have not created any{" "}
                              {eventTab === "Event" ? "event" : "MICE"} plan yet,
                              Please create it
                            </Text>
                          </Box>
                          <CustomButton
                            bgColor={COLORS.THEME_COLOR}
                            borderRadius="50px"
                            text={`Add ${eventTab} Plan`}
                            textColor={COLORS.WHITE_COLOR}
                            fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                            fontWeight={COMMONFONTWEIGHT.LARGE_FONT_WEIGHT}
                            width="auto"
                            mt={1}
                            py={2}
                            onPress={() => {
                              setSelectedDay(item[tabActiveButton]);
                              setSelectedAction("create_breakdown");
                              openModal();
                            }}
                          /> */}
                      </Box>
                    );
                } else {
                  return (
                    <Box
                      bg={MobileWindow && COLORS.WHITE_COLOR}
                      borderRadius={'8px'}
                      style={
                        MobileWindow && {
                          boxShadow:
                            '0px 18.953px 47.382px 0px rgba(119, 115, 170, 0.10);',
                        }
                      }
                      mt={
                        MobileWindow &&
                        (eventsDetail.from_date === eventsDetail.to_date
                          ? '27px'
                          : '10px')
                      }
                      pb={MobileWindow && 3}
                      alignItems={MobileWindow && 'center'}
                      width={'100%'}
                    >
                      <Box margin={'auto'} alignItems={'center'}>
                        {noPhotosUploadedSVG}
                      </Box>
                      <Box alignItems={'center'}>
                        <Text
                          fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                          fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                          color={COLORS.BLACK_COLOR}
                        >
                          No {eventTab === 'Event' ? 'event' : 'MICE'} plan
                          available
                        </Text>
                      </Box>
                    </Box>
                  );
                }
              } else {
                return !MobileWindow ? (
                  <Box px={'24px'} width={'100%'} position={'relative'}>
                    {currentDate[ind] &&
                      ((
                        hasPermission('ADD_EVENT_BREAKDOWN')) ||
                        (hasPermission('VENDOR') && selected)) &&
                      statusMap.indexOf(eventsDetail.status) < 3 && (
                        <Box
                          alignItems={'end'}
                          width={'95%'}
                          position={'absolute'}
                          style={{
                            transform: [{ translateX: 4 }, { translateY: -13 }],
                          }}
                          zIndex={'999'}
                        >
                          <Pressable
                            onPress={() => {
                              // Use the updated data from defaultData instead of the original item
                              const currentDayData = defaultData.find(day => Object.keys(day)[0] === tabActiveButton);
                              
                              if (currentDayData) {
                                setSelectedDay(currentDayData[tabActiveButton]);
                              } else {
                                setSelectedDay(item[tabActiveButton]);
                              }
                              setSelectedAction('edit_breakdown');
                              openModal();
                            }}
                          >
                            {editIconDetailsPage}
                          </Pressable>
                        </Box>
                      )}
                    {/* {item[tabActiveButton].agenda && (
                      <Box width={'90%'} mb={'21px'}>
                        <Text
                          fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
                          fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                          mb={4}
                        >
                          Agenda
                        </Text>
                        <Text
                          fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                          fontWeight={COMMONFONTWEIGHT.SMALL_FONT_WEIGHT}
                          color={COLORS.EVENT_BREAKDOWN_AGENDA_COLOR}
                        >
                          {`"${item[tabActiveButton].agenda}"`}
                        </Text>
                      </Box>
                    )} */}
                    {item &&
                      item[tabActiveButton] &&
                      item[tabActiveButton].agenda &&
                      item[tabActiveButton].agenda.length > 0 && (
                        <Box mb={'35px'}>
                          <Text
                            fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
                            fontWeight={COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT}
                            mb={4}
                            color={COLORS.BLACK_COLOR}
                          >
                            Agenda Details
                          </Text>
                          {renderAgendaWithSections(item[tabActiveButton].agenda)}
                        </Box>
                      )}
                    {/* {item &&
                      item[tabActiveButton] &&
                      item[tabActiveButton].menu &&
                      item[tabActiveButton].menu.some(
                        (obj) =>
                          (Array.isArray(obj.list) && obj.list.length > 0 &&
                            obj.list.some((value) => value !== '')) ||
                          obj.title !== ''
                      ) && ( */}
                        <Box mb={'35px'}>
                          <HStack
                            alignItems={'center'}
                            justifyContent={'space-between'}
                            width={'100%'}
                            mb={4}
                          >
                            <HStack alignItems={'center'} space={3}>
                              <Text
                                fontSize={COMMONFONTSIZE.EXTRA_LARGE_FONT_SIZE}
                                fontWeight={
                                  COMMONFONTWEIGHT.MEDIUM_LARGE_FONT_WEIGHT
                                }
                              >
                                Food Menu
                              </Text>
                              {foodMenuData && foodMenuData?.uploaded_at && (
                                <ToggleSwitch
                                  isOn={isFoodMenuVisible}
                                  onToggle={() => handleFoodMenuToggle(!isFoodMenuVisible)}
                                />
                              )}
                            </HStack>
                            <Pressable onPress={() => {
                              scrollToTop();
                              setShowBulkUploadModal(true);
                            }}>
                              <Text
                                fontSize={COMMONFONTSIZE.LARGE_FONT_SIZE}
                                color={COLORS.TABS_ACTIVE_BORDER_COLOR}
                                fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                              >
                                Bulk Upload
                              </Text>
                            </Pressable>
                          </HStack>
                          
                          {/* Display Food Menu from API */}
                          {!isFoodMenuVisible ? (
                            <Box mt={4}>
                              <Text
                                fontWeight={COMMONFONTWEIGHT.MEDIUM_FONT_WEIGHT}
                                fontSize={"20px"}
                                color={COLORS.ACCORDION_TITLE_COLOR}
                                textAlign={"center"}
                              >
                                Food Menu is hidden. Toggle On to see the results
                              </Text>
                            </Box>
                          ) : isLoadingFoodMenu ? (
                            <EventPlanDetailsCards
                              mt='3px'
                              body={
                                <VStack
                                  p={5}
                                  alignItems="center"
                                  justifyContent="center"
                                  minHeight="100px"
                                >
                                  <Text
                                    fontSize={COMMONFONTSIZE.MEDIUM_FONT_SIZE}
                                    color={COLORS.ATTACHMENTS_TEXT_COLOR}
                                  >
                                    Loading food menu...
                                  </Text>
                                </VStack>
                              }
                            />
                          ) : foodMenuData ? (
                            <Box mt={4}>
                              <FoodMenuCard menuData={foodMenuData} />
                            </Box>
                          ) : (
                            <Box mt={4}>
                              <NoDataFound 
                                customMessage={true}
                                dataName="food menu data"
                                image={false}
                              />
                            </Box>
                          )}
                        </Box>
                      {/* )} */}
                  </Box>
                ) : (
                  <VStack space={'16px'} width={'100%'} mt={4}>
                    {headings.map((head, ind) => {
                      if (
                        (head.value === 'agenda' &&
                          item[tabActiveButton][head.value]) ||
                        (head.value === 'menu' &&
                          item[tabActiveButton][head.value].some(
                            (obj) =>
                              (Array.isArray(obj.list) && obj.list.length > 0 &&
                                obj.list.some((value) => value !== '')) ||
                              obj.title !== ''
                          ))
                      )
                        return (
                          <AccordionExample
                            tabActiveButton={tabActiveButton}
                            item={item}
                            headings={head}
                            index={ind}
                            handleKnowMore={handleKnowMore}
                          />
                        );
                    })}
                  </VStack>
                );
              }
            }
          })}
      </VStack>
      {!MobileWindow ? (
        <CustomModal
          top={selectedAction !== 'speaker_detail' ? '30px' : '90%'}
          isOpen={showModal}
          onClose={closeModal}
          header={modalHeader()}
          body={showModal && modalChildren()}
          footer={modalFooter()}
          width='70%'
          height={
            selectedAction !== 'speaker_detail'
              ? eventTab === 'MICE'
                ? '60rem'
                : '55rem'
              : 'auto'
          }
          fixedPosition={selectedAction === 'speaker_detail' ? true : false}
          fixedTop={selectedAction === 'speaker_detail' ? 120 : undefined}
          maxH={selectedAction === 'speaker_detail' ? '35rem' : undefined}
        />
      ) : (
        <MobileModelSheet
          isOpen={showModal}
          onClose={closeModal}
          header={modalHeader()}
          body={modalChildren()}
          footer={modalFooter()}
        />
      )}

      {/* Access Control Modal */}
      {!MobileWindow ? (
        <CustomModal
          top='30px'
          isOpen={showAccessModal}
          onClose={closeAccessModal}
          header={getAccessModalHeader()}
          body={getAccessModalBody()}
          footer={getAccessModalFooter()}
          width='40%'
          height='auto'
        />
      ) : (
        <MobileModelSheet
          isOpen={showAccessModal}
          onClose={closeAccessModal}
          header={getAccessModalHeader()}
          body={getAccessModalBody()}
          footer={getAccessModalFooter()}
        />
      )}

      {/* Delete Confirmation Modal */}
      {!MobileWindow ? (
        <CustomModal
          isOpen={showDeleteModal}
          onClose={closeDeleteModal}
          header={getDeleteModalHeader()}
          body={getDeleteModalBody()}
          footer={getDeleteModalFooter()}
          width="40%"
          height="auto"
        />
      ) : (
        <MobileModelSheet
          isOpen={showDeleteModal}
          onClose={closeDeleteModal}
          header={getDeleteModalHeader()}
          body={getDeleteModalBody()}
          footer={getDeleteModalFooter()}
        />
      )}

      {/* Bulk Upload Menu Modal */}
      <BulkUploadMenuModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={async () => {
          // Refresh event details first
          await getEventDetails();
          // Then refresh food menu data after event details are updated
          await refreshFoodMenuData();
          // Force remount to refresh the component
          setForceRemount(!forceRemount);
          // Scroll to top
          window.scrollTo({ top: 0, behavior: 'smooth' });
          // Close the modal after successful upload
          setShowBulkUploadModal(false);
        }}
        eventId={eventsDetail?._id}
        dateOfEvent={(() => {
          if (eventsDetail?.from_date && tabActiveButton) {
            const eventStartDate = new Date(eventsDetail.from_date);
            const dayNumber = parseInt(tabActiveButton.split(' ')[1]) || 1;
            const eventDate = new Date(eventStartDate);
            eventDate.setDate(eventStartDate.getDate() + (dayNumber - 1));
            return eventDate.toISOString().split('T')[0];
          }
          return eventsDetail?.from_date;
        })()}
      />
    </VStack>
  );
};
export default EventBreakDownDetails;